import React from 'react'
import { Pressable, Text, View, useWindowDimensions } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { SCREEN_SIZE } from '@ui-kit/constants'
import { body2, caption } from '@ui-kit/core/typography'

import { radioCard } from './styles'
import type { NativeProps } from './types'
import { useTextProps } from './useTextProps'

const RadioCard = (props: NativeProps): JSX.Element => {
  const onPress = () => {
    if (props.disabled) {
      return
    }
    props.onPress(props.value)
  }

  const layout = props.layout ?? 'compact'

  const { width } = useWindowDimensions()

  const isSmallDevice = width <= (layout === 'compact' ? SCREEN_SIZE.S : SCREEN_SIZE.M)

  const { getTitleProps, getDescriptionsProps, getSubtitleProps } = useTextProps({
    layout,
    isSmallDevice,
    isSelected: props.isSelected,
    disabled: props.disabled,
  })
  const { container, card, iconContainer, textContainer, titleContainer, tag } = radioCard({
    hasTopTag: props.topTag !== undefined,
    hasBottomTag: props.bottomTag !== undefined,
    isSelected: props.isSelected,
    layout,
    disabled: props.disabled,
    isUntouched: props.isUntouched,
    hasBottomTags: props.hasBottomTags,
    hasTopTags: props.hasTopTags,
  })

  return (
    <View style={props.style} className={container()} id={props.testID}>
      {props.topTag !== undefined ? (
        <Pressable
          className={tag({
            className: twMerge('rounded-t-lg', props.topTag.onPress && 'active:opacity-70'),
          })}
          onPress={props.topTag.onPress}
        >
          <Text
            className={caption({
              align: 'center',
              color: props.isSelected ? 'uiLight' : undefined,
              emphasis: props.isSelected ? 'default' : 'medium',
              className: `line-clamp-1 ${props.topTag.onPress ? 'underline' : ''}`,
            })}
          >
            {props.topTag.label}
          </Text>
        </Pressable>
      ) : null}
      <Pressable className={card()} testID={`${props.testID}-Card`} onPress={onPress}>
        <View className={iconContainer()}>{props.icon}</View>
        <View className={textContainer()}>
          <View className={titleContainer()}>
            {layout === 'compact' && !isSmallDevice ? (
              <>
                <Text className={caption(getTitleProps())}>{props.title}</Text>
                {props.subtitle ? (
                  <Text className={caption(getSubtitleProps())}>{props.subtitle}</Text>
                ) : null}
              </>
            ) : null}
            {layout === 'wide' || isSmallDevice ? (
              <>
                <Text className={body2(getTitleProps())}>{props.title}</Text>
                {props.subtitle ? (
                  <Text className={body2(getSubtitleProps())}>{props.subtitle}</Text>
                ) : null}
              </>
            ) : null}
          </View>
          {props.description !== undefined ? (
            <Text className={caption({ ...getDescriptionsProps(), className: 'flex-1 flex-wrap' })}>
              {props.description}
            </Text>
          ) : null}
        </View>
      </Pressable>
      {props.bottomTag !== undefined ? (
        <Pressable
          className={tag({
            className: twMerge('rounded-b-lg', props.bottomTag.onPress && 'active:opacity-70'),
          })}
          onPress={props.bottomTag.onPress}
        >
          <Text
            className={caption({
              align: 'center',
              color: props.isSelected ? 'uiLight' : undefined,
              emphasis: props.isSelected ? 'default' : 'medium',
              className: `line-clamp-1 ${props.bottomTag.onPress ? 'underline' : ''}`,
            })}
          >
            {props.bottomTag.label}
          </Text>
        </Pressable>
      ) : null}
    </View>
  )
}

export default RadioCard
